import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import "./company.scss";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CircularProgress from "@mui/material/CircularProgress";
import {
  addNewCompany,
  editCompany,
  fetchCompanyById,
  fetchCustomer,
  fetchUserById,
} from "../../store/slices/thunk";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import {
  GoogleMap,
  Marker,
  Autocomplete as GMapAutocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import { getTenantConfigFromStorage } from "../../helpers/tenantConfig";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    height: "48px",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 1 },
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
    fontSize: "14px",
    "&::placeholder": { color: "#94a3b8", opacity: 1 },
  },
};

const AddnewCompanies = () => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places", "geometry"],
  });
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [allCustomer, setAllCustomer] = useState([]);
  const dispatch: any = useDispatch();
  const { id } = useParams<any>();
  const tenantConfig = getTenantConfigFromStorage();
  const schoolLabel = tenantConfig.labels.school;
  const isCompanyTenant = tenantConfig.mode === "company";
  const listingRoute = isCompanyTenant ? "/company-listing" : "/school-listing";
  const addRoute = isCompanyTenant ? "/add-new-company" : "/add-new-school";
  const editRoute = isCompanyTenant
    ? `/edit-new-company/${id}`
    : `/edit-new-school/${id}`;

  const navigate = useNavigate();
  const [addCompany, setAddCompany] = React.useState({
    companyName: "",
    contactNumber: "",
    status: "active",
    schoolLocation: {
      streetAddress: "",
      coordinates: [],
      type: "Point",
    },
    _id: "",
  });
  const [selectedPosition, setSelectedPosition] = useState<any>(null);
  const onLoadAutocomplete = (auto: any) => {
    setAutocomplete(auto);
  };
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const [locationObject, setLocationObject] = useState<any>(null);

  useEffect(() => {
    const getAllCustomer = async () => {
      const response: any = await dispatch(
        fetchCustomer(1, 99999999, "", "active", "")
      );
      if (response && response?.data?.data?.length > 0 && response?.isSuccess) {
        setAllCustomer(response?.data?.data);
      } else {
        setAllCustomer([]);
      }
    };
    getAllCustomer();
  }, [dispatch]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const { companyName, contactNumber, status, schoolLocation } = addCompany;
      if (
        companyName === "" ||
        contactNumber === "" ||
        status === "" ||
        schoolLocation?.coordinates?.length === 0
      ) {
        toast.error("Please fill all the required fields!");
        setLoading(false);
        return;
      }

      let res: any;
      if (isEdit) {
        res = await dispatch(editCompany(addCompany));
        let getUserFromStorage: any = localStorage.getItem("user");
        getUserFromStorage = JSON.parse(getUserFromStorage);
        let getUser = await dispatch(fetchUserById(getUserFromStorage._id));
        localStorage.setItem("user", JSON.stringify(getUser?.data));
        toast(`${schoolLabel} edited!`);
        navigate(listingRoute);
      } else {
        res = await dispatch(addNewCompany(addCompany));
        if (res.data && res.isSuccess) {
          let getUserFromStorage: any = localStorage.getItem("user");
          getUserFromStorage = JSON.parse(getUserFromStorage);
          let getUser = await dispatch(fetchUserById(getUserFromStorage._id));
          localStorage.setItem("user", JSON.stringify(getUser?.data));
          toast(`${schoolLabel} added!`);
          navigate(listingRoute);
        } else {
          toast(`${schoolLabel} not added!`);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast("Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        setIsEdit(true);
        const response: any = await dispatch(fetchCompanyById(id));
        setAddCompany(response?.data);
        navigate(editRoute);
      } else {
        setIsEdit(false);
        setAddCompany({
          companyName: "",
          contactNumber: "",
          schoolLocation: {
            streetAddress: "",
            coordinates: [],
            type: "Point",
          },
          status: "active",
          _id: "",
        });
        navigate(addRoute);
      }
    };
    fetchData();
  }, [id, navigate, dispatch]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setAddCompany((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const navigateToaddCompany = () => {
    navigate(listingRoute);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete?.getPlace();
      const lat = place?.geometry?.location?.lat();
      const lng = place?.geometry?.location?.lng();

      if (lat && lng) {
        setLocationObject({
          lat,
          lng,
          streetAddress: place?.formatted_address || place?.name || "",
        });
        setAddCompany((prevData: any) => ({
          ...prevData,
          schoolLocation: {
            streetAddress: place?.formatted_address || place?.name || "",
            coordinates: [lng, lat],
            type: "Point",
          },
        }));
        setSelectedPosition({ lat, lng });
      }
    }
  };

  if (loadError) return <div>Map load error: {String(loadError)}</div>;
  if (!isLoaded) return <div>Loading Google Maps...</div>;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "transparent",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: -50,
          right: -50,
          width: 500,
          height: 500,
          borderRadius: "50%",
          
          pointerEvents: "none",
        },
      }}
    >
      <ToastContainer />
      <Card
        sx={{
          maxWidth: 1900,
          mx: "auto",
          mt: 4,
          mb: 4,
          borderRadius: 4,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          border: "1px solid #eef0f5",
          position: "relative",
          zIndex: 1,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconButton
                onClick={navigateToaddCompany}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  width: 36,
                  height: 36,
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 18, color: "#555" }} />
              </IconButton>
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#1e90ff" }}
                >
                  {isEdit ? `Edit ${schoolLabel}` : `Add New ${schoolLabel}`}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {isEdit
                    ? `Update ${schoolLabel.toLowerCase()} information and parameter details.`
                    : `Create a new ${schoolLabel.toLowerCase()} profile and assign passengers`}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ borderBottom: "1px solid #eef0f5", my: 2 }} />

          {/* Form Fields */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Company Name */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#344054",
                  mb: 1,
                  fontSize: "0.85rem",
                }}
              >
                {schoolLabel} Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                name="companyName"
                placeholder={`Enter ${schoolLabel.toLowerCase()} name`}
                value={addCompany.companyName}
                onChange={handleChange}
                sx={textFieldSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          backgroundColor: "#E3F2FD",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#2196F3"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="2"
                            y="7"
                            width="20"
                            height="14"
                            rx="2"
                            ry="2"
                          />
                          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                        </svg>
                      </Box>
                    ),
                  },
                }}
              />
            </Box>

            {/* Contact Number */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#344054",
                  mb: 1,
                  fontSize: "0.85rem",
                }}
              >
                Contact Number <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                name="contactNumber"
                placeholder="Enter contact number"
                value={addCompany.contactNumber}
                onChange={handleChange}
                sx={textFieldSx}
                slotProps={{
                  input: {
                    startAdornment: (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          backgroundColor: "#E3F2FD",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#2196F3"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </Box>
                    ),
                  },
                }}
              />
            </Box>

            {/* Status */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#344054",
                  mb: 1,
                  fontSize: "0.85rem",
                }}
              >
                Status <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                select
                fullWidth
                name="status"
                value={addCompany.status}
                onChange={handleChange}
                sx={{
                  ...textFieldSx,
                  "& .MuiOutlinedInput-root": {
                    ...textFieldSx["& .MuiOutlinedInput-root"],
                    height: "48px",
                  },
                }}
              >
                <MenuItem value="active">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#22c55e",
                      }}
                    />
                    Active
                  </Box>
                </MenuItem>
                <MenuItem value="inActive">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#ef4444",
                      }}
                    />
                    In Active
                  </Box>
                </MenuItem>
              </TextField>
            </Box>
          </Box>

          {/* Address Section */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a", mb: 2 }}
            >
              Address <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ mb: 2 }}>
              <GMapAutocomplete
                onLoad={onLoadAutocomplete}
                onPlaceChanged={onPlaceChanged}
                options={{
                  componentRestrictions: { country: "pk" },
                  types: ["geocode", "establishment"],
                  fields: [
                    "address_components",
                    "geometry",
                    "icon",
                    "name",
                    "formatted_address",
                  ],
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Search location..."
                  value={locationObject?.streetAddress || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setLocationObject(null);
                      setSelectedPosition(null);
                    } else {
                      setLocationObject((prev: any) => ({
                        ...prev,
                        streetAddress: value,
                      }));
                    }
                  }}
                  sx={textFieldSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                          backgroundColor: "#E3F2FD",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 1,
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#2196F3"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          </svg>
                        </Box>
                      ),
                    },
                  }}
                />
              </GMapAutocomplete>
            </Box>
            <Box
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #e0e0e0",
              }}
            >
              <GoogleMap
                center={
                  selectedPosition || { lat: 24.8607, lng: 67.0011 }
                }
                zoom={selectedPosition ? 14 : 10}
                mapContainerStyle={{ width: "100%", height: "300px" }}
              >
                {selectedPosition && <Marker position={selectedPosition} />}
              </GoogleMap>
            </Box>
          </Box>

          {/* Submit Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    <path d="m15 5 4 4" />
                  </svg>
                )
              }
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1.2,
                backgroundColor: "#2563EB",
                boxShadow: "0 4px 14px rgba(0, 102, 255, 0.2)",
                "&:hover": {
                  backgroundColor: "#0052cc",
                },
              }}
            >
              {loading
                ? isEdit
                  ? "Editing..."
                  : "Adding..."
                : isEdit
                ? `Edit ${schoolLabel}`
                : `Add ${schoolLabel}`}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddnewCompanies;
